import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";

import CheckCircleIcon from "@material-ui/icons/CheckCircleOutline";
import ReceiptIcon from "@material-ui/icons/Receipt";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import CancelIcon from "@material-ui/icons/HighlightOff";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import StatusChip from "../../components/StatusChip";
import useConfirm from "../../hooks/useConfirm";
import { displayClientName } from "../../utils/clientName";
import { ORDER_STATUS_META, fmtMoney } from "../Orders";

/**
 * Rótulo e consequência de cada transição.
 *
 * A consequência aparece no diálogo de confirmação porque essas ações mexem em
 * dinheiro: "Faturar" gera cobrança de verdade, "Cancelar" derruba as contas em
 * aberto. Quem clica precisa saber disso antes, não depois.
 */
const ACTIONS = {
  confirmed: {
    label: "Confirmar",
    icon: <CheckCircleIcon />,
    variant: "contained",
    message:
      "O pedido passa a valer como acordo fechado e os itens não podem mais ser alterados.",
    destructive: false,
  },
  invoiced: {
    label: "Faturar",
    icon: <ReceiptIcon />,
    variant: "contained",
    message:
      "Gera as contas a receber do pedido, de acordo com o parcelamento definido. Isto cobra o cliente.",
    destructive: false,
  },
  delivered: {
    label: "Marcar entregue",
    icon: <LocalShippingIcon />,
    variant: "outlined",
    message: "Registra a entrega. Não altera nada no financeiro.",
    destructive: false,
  },
  canceled: {
    label: "Cancelar pedido",
    icon: <CancelIcon />,
    variant: "outlined",
    message:
      "As contas a receber ainda em aberto são canceladas junto. Parcelas já pagas não são tocadas.",
    destructive: true,
  },
};

const useStyles = makeStyles((theme) => ({
  body: { flex: 1, minHeight: 0, overflow: "auto", paddingTop: theme.spacing(2) },
  card: { padding: theme.spacing(2), height: "100%" },
  label: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: theme.palette.text.secondary,
    display: "block",
  },
  value: { fontSize: 14, fontWeight: 500 },
  money: { fontVariantNumeric: "tabular-nums" },
  totals: {
    marginTop: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 4,
  },
  totalLine: { display: "flex", gap: theme.spacing(3), fontVariantNumeric: "tabular-nums" },
  grand: { fontSize: 18, fontWeight: 700 },
  section: { marginTop: theme.spacing(2), padding: theme.spacing(2) },
  muted: { color: theme.palette.text.hint },
}));

const Field = ({ label, children, className }) => (
  <div style={{ marginBottom: 12 }}>
    <span className={className}>{label}</span>
    <div>{children}</div>
  </div>
);

const OrderDetail = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const [confirm, confirmDialog] = useConfirm();

  const [order, setOrder] = useState(null);
  const [transitions, setTransitions] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
    } catch (err) {
      toastError(err);
      history.push("/orders");
    }
  }, [id, history]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // As transições permitidas vêm do backend para a regra não viver em dois
    // lugares: se lá "faturado" deixar de ir para "entregue", o botão some
    // sozinho aqui.
    api
      .get("/orders/meta")
      .then(({ data }) => setTransitions(data?.transitions || {}))
      .catch(() => {});
  }, []);

  const changeStatus = async (to) => {
    const meta = ACTIONS[to];
    const ok = await confirm({
      title: `${meta.label} o pedido ${order.number}?`,
      message: meta.message,
      confirmLabel: meta.label,
      destructive: meta.destructive,
    });
    if (!ok) return;

    setBusy(true);
    try {
      const { data } = await api.patch(`/orders/${id}/status`, { status: to });
      setOrder(data);
      toast.success(`Pedido ${meta.label.toLowerCase()}.`);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  if (!order) {
    return (
      <MainContainer>
        <PageHeader
          title="Carregando…"
          onBack={() => history.push("/orders")}
          backLabel="Voltar para Pedidos"
        />
      </MainContainer>
    );
  }

  const statusMeta = ORDER_STATUS_META[order.status] || {
    label: order.status,
    tone: "neutral",
  };
  const next = transitions[order.status] || [];
  const items = order.items || [];

  return (
    <MainContainer>
      {confirmDialog}
      <PageHeader
        title={`Pedido ${order.number}`}
        subtitle={displayClientName(order.client, `Cliente #${order.clientId}`)}
        onBack={() => history.push("/orders")}
        backLabel="Voltar para Pedidos"
        actions={
          <>
            <StatusChip dot tone={statusMeta.tone} label={statusMeta.label} />
            {next.map((to) => {
              const meta = ACTIONS[to];
              if (!meta) return null;
              return (
                <Button
                  key={to}
                  variant={meta.variant}
                  color={meta.destructive ? "default" : "primary"}
                  startIcon={meta.icon}
                  disabled={busy}
                  onClick={() => changeStatus(to)}
                >
                  {meta.label}
                </Button>
              );
            })}
          </>
        }
      />

      <div className={classes.body}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Paper variant="outlined" className={classes.card}>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Itens
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Descrição</TableCell>
                    <TableCell align="right">Qtd</TableCell>
                    <TableCell align="right">Unitário</TableCell>
                    <TableCell align="right">Desconto</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.id}>
                      <TableCell>{it.description}</TableCell>
                      <TableCell align="right" className={classes.money}>
                        {Number(it.quantity)}
                      </TableCell>
                      <TableCell align="right" className={classes.money}>
                        {fmtMoney(it.unitPrice, order.currency)}
                      </TableCell>
                      <TableCell align="right" className={classes.money}>
                        {Number(it.discount) > 0
                          ? fmtMoney(it.discount, order.currency)
                          : "—"}
                      </TableCell>
                      <TableCell align="right" className={classes.money}>
                        {fmtMoney(it.total, order.currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!items.length && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" className={classes.muted}>
                        Nenhum item neste pedido.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <div className={classes.totals}>
                <div className={classes.totalLine}>
                  <span>Subtotal</span>
                  <span>{fmtMoney(order.subtotal, order.currency)}</span>
                </div>
                {Number(order.discountAmount) > 0 && (
                  <div className={classes.totalLine}>
                    <span>Desconto</span>
                    <span>− {fmtMoney(order.discountAmount, order.currency)}</span>
                  </div>
                )}
                <div className={`${classes.totalLine} ${classes.grand}`}>
                  <span>Total</span>
                  <span>{fmtMoney(order.totalAmount, order.currency)}</span>
                </div>
              </div>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper variant="outlined" className={classes.card}>
              <Typography
                variant="subtitle1"
                style={{ fontWeight: 600, marginBottom: 12 }}
              >
                Condições
              </Typography>

              <Field label="Pagamento" className={classes.label}>
                <span className={classes.value}>
                  {order.paymentMethod || (
                    <span className={classes.muted}>não definido</span>
                  )}
                </span>
              </Field>

              <Field label="Parcelamento" className={classes.label}>
                <span className={classes.value}>
                  {Number(order.installments) > 1
                    ? `${order.installments}x de ${fmtMoney(
                        Number(order.totalAmount) / Number(order.installments),
                        order.currency
                      )}`
                    : "À vista"}
                </span>
              </Field>

              <Field label="Primeiro vencimento" className={classes.label}>
                <span className={classes.value}>
                  {order.firstDueDate ? (
                    new Date(`${order.firstDueDate}T12:00:00`).toLocaleDateString(
                      "pt-BR"
                    )
                  ) : (
                    <span className={classes.muted}>
                      hoje, no faturamento
                    </span>
                  )}
                </span>
              </Field>

              <Field label="Entrega prevista" className={classes.label}>
                <span className={classes.value}>
                  {order.expectedDeliveryDate ? (
                    new Date(
                      `${order.expectedDeliveryDate}T12:00:00`
                    ).toLocaleDateString("pt-BR")
                  ) : (
                    <span className={classes.muted}>—</span>
                  )}
                </span>
              </Field>

              {order.proposal?.number && (
                <Field label="Origem" className={classes.label}>
                  <span className={classes.value}>
                    Proposta {order.proposal.number}
                  </span>
                </Field>
              )}

              {order.cancelReason && (
                <Field label="Motivo do cancelamento" className={classes.label}>
                  <span className={classes.value}>{order.cancelReason}</span>
                </Field>
              )}
            </Paper>
          </Grid>
        </Grid>
      </div>
    </MainContainer>
  );
};

export default OrderDetail;
